import React from 'react';
import {
    Responsive,
    SimpleList,
    SimpleShowLayout,
    Datagrid,
    SimpleForm,
    TextField,
    BooleanField,
    TextInput,
    SelectInput,
    DisabledInput,
    required
} from 'admin-on-rest';
import List from '../admin/ListStatic';
import Show from '../admin/Show';
import Create from '../admin/Create';
import Edit from '../admin/Edit';
import ViewButton from '../buttons/ViewButton';
import EditButton from '../buttons/EditButton';
import ValidatedInputField from '../components/ValidatedInputField';
import TooltipIcon from '../components/TooltipIcon';
import ReactTooltip from 'react-tooltip';
import {passwordValidation, passwordChangeValidation} from "../shared/validation";
import Avatar from 'material-ui/Avatar';
import Icon from 'material-ui/svg-icons/social/people-outline';
import FuseTheme from "../global/theme";

export const fuseSysOpIcon = Icon;

const styles = {
    accessBox: {
        display: 'block',
        padding: '4px 10px 4px 10px',
        border: '1px solid ' + FuseTheme.palette.lightTextColor,
        boxSizing: 'border-box',
    },
    avatar: {backgroundColor: "#6e6e6e", width: 40, height: 40, marginRight: 10, verticalAlign: 'bottom'},
    description: {lineHeight: 1.5},
    row: {display: 'flex', justifyContent: 'space-between'},
    rowField: {flex: '0 1 30%', display: 'inline-block', margin: '0 1em 0 0', verticalAlign: 'top'},
    rowShowField: {display: 'inline-block', width: 'auto', minWidth: 100, margin: '0 1em 0 0', verticalAlign: 'top'},
    fieldSmall: {flex: '0 1 10%', display: 'inline-block', maxWidth: '8.5em', overflow: 'hidden'},
    fieldHeader: {
        display: 'block',
        width: '100%',
        fontSize: '16px',
        fontWeight: 'bold',
        color: FuseTheme.palette.lightTextColor,
        margin: '2em 0 0 0',
    },
    fieldInstructions: {
        display: 'block',
        width: '100%',
        fontSize: '14px',
        fontWeight: 'bold',
        color: FuseTheme.palette.lightTextColor,
        margin: '2em 0 0 0',
    },
    fieldLabel: {
        display: 'block',
        width: '100%',
        fontSize: '14px',
        color: FuseTheme.palette.lightTextColor,
        margin: '2em 0 0 0',
    },
    fieldDescription: {
        flex: '1 1 40%',
        display: 'inline-block',
        width: 'auto',
        height: '2em',
        fontSize: '12px',
        color: FuseTheme.palette.lightTextColor,
        verticalAlign: 'bottom',
    },
    divider: {
        display: 'block',
        width: '100%',
        height: 1,
        backgroundColor: FuseTheme.palette.primary3Color,
        border: 'none',
        margin: '1em 0 0.5em 0'
    },
    hidden: {display: 'none'},
    break: {display: 'block', width: '100%', height: 0, border: 'none', margin: 0},
    paddingBottom: {paddingBottom: '1em'},
};

const validatePassword = [required, passwordValidation];
const validateChangePassword = [passwordChangeValidation];

/*
 * View: List
 */

// Set title for List Sysops
const ListTitle = <span><Avatar icon={<Icon/>} style={styles.avatar} className="avatar-title"/>System Operators</span>;

const ListDescription = () => {
    return (
        <span style={styles.description}>
            <p>A System Operator (Sysop) has access to all Systems, and full access to CloudSent Admin and Reports.
                Sysops have the ability to create, view, edit, and mark as inactive all Users and Sysadmins.</p>
            <p>Sysops can create Systems and assign access to sections.</p>
        </span>
    );
};

/*
 * List Sysops table
 */
export const fuseSystemOperatorList = (props) => (
    <List {...props} title={ListTitle} desc={<ListDescription/>} sort={{field: 'email', order: 'ASC'}}
          perPage={20} filter={{role: 'sys_op'}}>
        <Responsive
            small={
                <SimpleList
                    primaryText={record => record.email}
                    secondaryText={record => record.first_name + '  ' + record.last_name}
                    tertiaryText={record => record.active}
                />
            }
            medium={
                <Datagrid bodyOptions={{stripedRows: false, showRowHover: true}}>
                    <TextField source="email" label="Email"/>
                    <TextField source="first_name" label="First Name"/>
                    <TextField source="last_name" label="Last Name"/>
                    <BooleanField source="active" label="Active"/>
                    <ViewButton/>
                    <EditButton/>
                </Datagrid>
            }
        />
    </List>
);

/*
 * View: Show
 */

// Set title for Show Sysop
const ShowTitle = ({record}) => {
    let firstName, lastName;
    if (record) {
        firstName = (record.sys_first_name) ? record.sys_first_name : record.first_name;
        lastName = (record.sys_last_name) ? record.sys_last_name : record.last_name;
    }
    return <span><Avatar icon={<Icon/>} style={styles.avatar}
                         className="avatar-title"/>{record ? `${firstName} ${lastName}` : 'Sysop'}</span>;
};

const ShowDescription = () => {
    return (
        <span style={styles.description}>
            <p>Review details below. To edit this Sysop, click <strong>Edit</strong> above.</p>
        </span>
    );
};

/*
 * Show Sysop Table
 */
export const fuseSystemOperatorShow = (props) => (
    <Show title={<ShowTitle/>} desc={<ShowDescription/>} {...props}>
        <SimpleShowLayout>
            <TextField source="sys_email" label="Email"/>
            <TextField source="sys_first_name" label="First Name"/>
            <TextField source="sys_last_name" label="Last Name"/>
            <BooleanField source="active" label="Active" style={styles.rowShowField} elStyle={styles.fieldSmall}/>
            <BooleanField source="locked" label="Locked" style={styles.rowShowField} elStyle={styles.fieldSmall}/>
        </SimpleShowLayout>
    </Show>
);

/*
 * View: Create form
 */

// Set title for Create Sysop
const CreateTitle = <span><Avatar icon={<Icon/>} style={styles.avatar}
                                  className="avatar-title"/>Create a Sysop</span>;

const CreateDescription = () => {
    return (
        <span style={styles.description}>
            <p>A System Operator (Sysop) has access to all Systems, and full access to CloudSent Admin and Reports.
                Sysops have the ability to create, view, edit, and mark as inactive all Users and Sysadmins.</p>
            <p>Sysops can create Systems and assign access to sections.</p>
            <p>Enter the required fields below.</p>
        </span>
    );
};

// Set defaults for Create form
const defaultCreateValues = {
    active: false,
    locked: false,
    sys_op: true,
    sys_admin: false,
    systems_all: true,
    systems: [],
    sections_all: true,
    sections: [],
    locations_all: true,
    locations: [],
    dbas_all: true,
    dbas: [],
    districts_all: true,
    districts: [],
};

/*
 * Create Sysop form
 */
export const fuseSystemOperatorCreate = (props) => (
    <Create {...props} title={CreateTitle} desc={<CreateDescription/>}>
        <SimpleForm defaultValue={defaultCreateValues} submitOnEnter={false} redirect="list">

            <hr style={{...styles.break, ...styles.paddingBottom}}/>
            <span style={styles.fieldDescription}>
                Enter and verify a unique <strong>Email Address</strong> for this Sysop.
            </span>
            <ValidatedInputField
                formName="record-form"
                source="sys_email"
                fieldType="email"
                label="Email Address*"
                buttonLabel="Check Availability"
                successMessage="This Email is available."
                errorDuplicateMessage="A User with this Email already exists."
                errorRequiredMessage="A unique Email is required."
                fieldResource="Sysops"
                isRequired={true}
            />

            <TextInput source="sys_password" label="Password" type="password" validate={validatePassword}/>
            <TextInput source="sys_confirm_password" label="Confirm Password" type="password" validate={validatePassword}/>
            <TextInput source="sys_first_name" label="First Name" validate={[required]} style={styles.rowShowField}/>
            <TextInput source="sys_last_name" label="Last Name" validate={[required]} style={styles.rowShowField}/>
            <hr style={styles.break}/>

            <span style={styles.fieldLabel}>
                Sysop is Active*
                <TooltipIcon dataFor="ttActive"/>
                <ReactTooltip id="ttActive" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    If <em>Active</em>, this Sysop account can access and modify Users and Systems.
                    <br/>
                    If <em>Inactive</em>, this Sysop account cannot access or modify Users and Systems.
                </ReactTooltip>
            </span>
            <SelectInput source="active"
                         validate={[required]}
                         choices={[
                             {id: true, name: 'Active'},
                             {id: false, name: 'Inactive'},
                         ]}
                         options={{
                             floatingLabelText: '',
                         }}
            />

            <span style={styles.fieldInstructions}>
                After clicking <strong>Save</strong>, you will be redirected to the Sysops List.
            </span>
            <hr style={{...styles.break, ...styles.paddingBottom}}/>
        </SimpleForm>
    </Create>
);

/*
 * View: Edit form
 */

// Set title for Edit Sysop
const EditTitle = ({record}) => {
    let firstName, lastName;
    if (record) {
        firstName = (record.sys_first_name) ? record.sys_first_name : record.first_name;
        lastName = (record.sys_last_name) ? record.sys_last_name : record.last_name;
    }

    return <span><Avatar icon={<Icon/>} style={styles.avatar}
                         className="avatar-title"/>Edit {record ? `${firstName} ${lastName}` : 'Sysop'}</span>;
};

const EditDescription = () => {
    return (
        <span style={styles.description}>
            <p>Modify fields in the tabs below. Click <strong>Save</strong> to store this Sysop's details.</p>
        </span>
    );
};

/*
 * Edit Sysop form
 */
export const fuseSystemOperatorEdit = (props) => (
    <Edit {...props} title={<EditTitle/>} desc={<EditDescription/>}>
        <SimpleForm submitOnEnter={false}>
            <DisabledInput source="sys_email" label="Email"/>
            <span style={styles.fieldDescription}>
                Email cannot be edited.
                <TooltipIcon dataFor="ttEmail"/>
                <ReactTooltip id="ttEmail" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    Email addresses must be unique within the system. A Sysop's email cannot be edited.
                </ReactTooltip>
            </span>
            <hr style={styles.break}/>
            <TextInput source="sys_first_name" label="First Name" validate={[required]} style={styles.rowShowField}/>
            <TextInput source="sys_last_name" label="Last Name" validate={[required]} style={styles.rowShowField}/>
            <hr style={styles.break}/>

            <span style={styles.fieldLabel}>
                Sysop is Active*
                <TooltipIcon dataFor="ttActive"/>
                <ReactTooltip id="ttActive" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    If <em>Active</em>, this Sysop account can access and modify Users and Systems.
                    <br/>
                    If <em>Inactive</em>, this Sysop account cannot access or modify Users and Systems.
                </ReactTooltip>
            </span>
            <SelectInput source="active"
                         validate={[required]}
                         choices={[
                             {id: true, name: 'Active'},
                             {id: false, name: 'Inactive'},
                         ]}
                         options={{
                             floatingLabelText: '',
                         }}
            />

            <span style={styles.fieldLabel}>
                Lock this Sysop Account
                <TooltipIcon dataFor="ttLocked"/>
                <ReactTooltip id="ttLocked" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    If <em>Locked</em>, this Sysop account cannot access or modify Users and Systems.
                </ReactTooltip>
            </span>
            <SelectInput source="locked"
                         validate={[required]}
                         choices={[
                             {id: true, name: 'Account is Locked'},
                             {id: false, name: 'Account is not Locked'},
                         ]}
                         options={{
                             floatingLabelText: '',
                         }}
            />

            <hr style={{...styles.break, ...styles.paddingBottom}}/>

            <span style={styles.fieldLabel}>
                Change Sysop Password (if desired):
                <TooltipIcon dataFor="ttPass"/>
                <ReactTooltip id="ttPass" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    Modify these fields only if you wish to save a new password.
                </ReactTooltip>
            </span>
            <TextInput source="sys_new_password" label="Change Password" type="password" validate={validateChangePassword}/>
            <TextInput source="sys_confirm_new_password" label="Confirm Change Password" type="password"
                       validate={validateChangePassword}/>

            <span style={styles.fieldInstructions}>
                After clicking <strong>Save</strong>, you will be redirected to the Sysops List.
            </span>
            <hr style={{...styles.break, ...styles.paddingBottom}}/>
        </SimpleForm>
    </Edit>
);



// WEBPACK FOOTER //
// ./src/Fuse/System/sysOp.js